import { useTranslation } from "react-i18next";
import { SetStateAction, useEffect, useMemo, useState } from "react";
import {
	AdminViewOrderStatus,
	OrderStatus,
	Severity,
	WeightUnit,
} from "node-library/dist/constants";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { useSnackbar } from "../../../../common-components/toast/showToastMessage";
import { FrontendOrder } from "node-library/dist/portal";
import {
	getAdminStatus,
	getAdminStatusPermission,
	getAdminStatusStringByAdminStatus,
	getOrderStatusFromAdminStatus,
} from "../../../../helpers/orderHelper";
import { RowDropdown } from "../../../../common-components/inputs";
import { ModalWithButtons } from "../../../../common-components/modals/modal";
import { BlueButton } from "../../../../common-components/button";
import {
	CustomDatePicker,
	CustomDateTimePicker,
} from "../../../../common-components/datePicker";
import { sendPOSTRequest } from "../../../../requestHelper/sendRequest";
import { GreenTickIcon } from "../../../../common-components/toast/successIcons";
import { RedErrorIcon } from "../../../../common-components/toast/errorIcons";
import { isValid } from "date-fns";
import { ButtonTextLoadingAnimation } from "../../../../common-components/animation/ButtonLoadingAnimation";
import { Permission } from "../../../../permissionProvider/PermissionProvider";
import Restricted from "../../../../permissionProvider/Restricted";
import usePermission from "../../../../permissionProvider/usePermission";

interface Props {
	open: boolean;
	setOpen: React.Dispatch<React.SetStateAction<boolean>>;
	orderId: string;
	orderStatus: OrderStatus;
	setOrderStatus: React.Dispatch<React.SetStateAction<OrderStatus>>;
	orderViewedByAdmin: boolean;
	isOrderPaid: boolean;
	setOrderViewedByAdmin: React.Dispatch<React.SetStateAction<boolean>>;
	paymentTime: Date | undefined;
	setPaymentTime: React.Dispatch<React.SetStateAction<Date | undefined>>;
	assignTime: Date | undefined;
	setAssignTime: React.Dispatch<React.SetStateAction<Date | undefined>>;
	expectedCompleteTime: Date | undefined;
	setExpectedCompleteTime: React.Dispatch<
		React.SetStateAction<Date | undefined>
	>;
	shipTime: Date | undefined;
	setShipTime: React.Dispatch<React.SetStateAction<Date | undefined>>;
	deliverTime: Date | undefined;
	setDeliverTime: React.Dispatch<React.SetStateAction<Date | undefined>>;
	refreshSelectedOrder?: () => Promise<void>;
}

export default function EditOrderStateTimeModal(props: Props) {
	const { t: translation } = useTranslation();
	const { openSnackbar } = useSnackbar();

	const {
		orderId,
		orderStatus,
		orderViewedByAdmin,
		isOrderPaid,
		paymentTime,
		assignTime,
		expectedCompleteTime,
		shipTime,
		deliverTime,
		open,
		setOpen,
	} = props;

	const [currentAdminOrderStatus, setCurrentAdminOrderStatus] =
		useState<AdminViewOrderStatus>(
			getAdminStatus(orderStatus, isOrderPaid) as AdminViewOrderStatus
		);

	const [payDate, setPayDate] = useState<Date | null>(paymentTime || null);

	const [expectedCompleteDate, setExpectedCompleteTime] = useState<Date | null>(
		expectedCompleteTime || null
	);

	// const [assignDate, setAssignDate] = useState<Date | null>(assignTime || null);
	const [shipDate, setShipDate] = useState<Date | null>(shipTime || null);
	const [deliverDate, setDeliverDate] = useState<Date | null>(
		deliverTime || null
	);

	//validation
	const [payTimeError, setPayTimeError] = useState<string>("");

	const [packageReceivedTimeError, setPackageReceivedTimeError] =
		useState<string>("");
	const [completeTimeError, setCompleteTimeError] = useState<string>("");

	const [shipTimeError, setShipTimeError] = useState<string>("");

	const [deliverTimeError, setDeliverTimeError] = useState<string>("");
	// 		const [unpaidAvailability, setUnpaidAvailability] =
	// 			useState<boolean>();
	// const [paidAvailability, setPaidAvailability] = useState<boolean>();
	// const [processingAvailability, setProcessingAvailability] = useState<boolean>();
	// 			const [readyAvailability, setReadyAvailability] =
	// 				useState<boolean>();
	// 			const [shippedAvailability, setShippedAvailability] =
	// 				useState<boolean>(true);
	// 			const [receivedAvailability, setreceivedAvailability] =
	// 				useState<boolean>(true);

	const [loading, setLoading] = useState<boolean>(false);

	function changeAdminOrderStatus(status: AdminViewOrderStatus) {
		setCurrentAdminOrderStatus(status);
		switch (status) {
			case AdminViewOrderStatus.UNPAID:
				setPayDate(null);
				// setAssignDate(null);
				setExpectedCompleteTime(null);
				setShipDate(null);
				setDeliverDate(null);
				break;
			case AdminViewOrderStatus.PAID:
				setPayDate(new Date());
				// setAssignDate(null);
				setExpectedCompleteTime(null);
				setShipDate(null);
				setDeliverDate(null);
				break;
			case AdminViewOrderStatus.IN_PROGRESS:
				// setAssignDate(new Date());
				setExpectedCompleteTime(null);
				setShipDate(null);
				setDeliverDate(null);
				break;
			// case AdminViewOrderStatus.READY_TO_SHIP:
			// 	setCompleteDate(new Date());
			// 	setShipDate(null);
			// 	setReceiveDate(null);
			// 	break;
			case AdminViewOrderStatus.SHIPPED:
				setShipDate(new Date());
				setDeliverDate(null);
				break;

			case AdminViewOrderStatus.DELIVERED:
				setDeliverDate(new Date());
				break;
		}
	}

	const statusSelectionDropdown: React.ReactNode = (
		<RowDropdown
			id="order-status-selection-dropdown"
			className="order-status-selection-dropdown"
			label={translation("OrderStatus")}
			options={Object.values(AdminViewOrderStatus).map((value) => ({
				value: value,
				title: getAdminStatusStringByAdminStatus(value, translation),
			}))}
			value={currentAdminOrderStatus}
			setValue={(status) => {
				changeAdminOrderStatus(status);
			}}
		></RowDropdown>
	);

	const fieldsForDates: Array<any> = [
		{
			id: "pay-time-picker",
			label: `${translation("PayOn")}`,
			value: payDate,
			setValue: setPayDate,
			dateOnly: true,
			disabled: currentAdminOrderStatus === AdminViewOrderStatus.UNPAID,
			permission: Permission.PRICE,
			errorMsg: payTimeError,
			setErrorMsg: setPayTimeError,
		},
		// {
		// 	id: "engineer-assigned-time-picker",
		// 	label: `${translation("EngineerAssigned")}`,
		// 	value: assignDate,
		// 	setValue: setAssignDate,
		// 	dateOnly: true,
		// 	disabled:
		// 		currentAdminOrderStatus === AdminViewOrderStatus.UNPAID ||
		// 		currentAdminOrderStatus === AdminViewOrderStatus.PAID,
		// 	permission: Permission.ENGINEER,
		// 	errorMsg: packageReceivedTimeError,
		// 	setErrorMsg: setPackageReceivedTimeError,
		// },
		{
			id: "order-expected-complete-date-picker",
			label: `${translation("EstimatedCompletedOn")}`,
			value: expectedCompleteDate,
			setValue: setExpectedCompleteTime,
			dateOnly: true,
			disabled:
				currentAdminOrderStatus === AdminViewOrderStatus.UNPAID ||
				currentAdminOrderStatus === AdminViewOrderStatus.PAID,
			permission: Permission.ENGINEER,
			errorMsg: completeTimeError,
			setErrorMsg: setCompleteTimeError,
		},
		{
			id: "ship-time-picker",
			label: `${translation("ShipOn")}`,
			value: shipDate,
			setValue: setShipDate,
			dateOnly: true,
			disabled:
				currentAdminOrderStatus === AdminViewOrderStatus.UNPAID ||
				currentAdminOrderStatus === AdminViewOrderStatus.PAID ||
				currentAdminOrderStatus === AdminViewOrderStatus.IN_PROGRESS,
			// currentAdminOrderStatus === AdminViewOrderStatus.READY_TO_SHIP,
			permission: Permission.SHIPPING,
			errorMsg: shipTimeError,
			setErrorMsg: setShipTimeError,
		},
		{
			id: "order-receive-date-picker",
			label: `${translation("ClientReceived")}`,
			value: deliverDate,
			setValue: setDeliverDate,
			dateOnly: true,
			disabled: currentAdminOrderStatus !== AdminViewOrderStatus.DELIVERED,
			permission: Permission.ADMIN,
			errorMsg: deliverTimeError,
			setErrorMsg: setDeliverTimeError,
		},
	];

	async function handleSubmit() {
		for (const field of fieldsForDates) {
			if (!field.disabled && !field.value) {
				field.setErrorMsg(translation("InvalidDate"));
				return;
			}
			if (field.value && !isValid(field.value)) {
				field.setErrorMsg(translation("InvalidDate"));
				return;
			} else {
				field.setErrorMsg("");
			}
		}
		try {
			setLoading(true);
			const data = await sendPOSTRequest("/orders/update-order-status-time", {
				orderId: orderId,
				adminOrderStatus: currentAdminOrderStatus,
				paymentTime: payDate,
				// assignTime: assignDate,
				expectedCompleteTime: expectedCompleteDate,
				shipTime: shipDate,
				deliverTime: deliverDate,
			});

			await props.refreshSelectedOrder!();
			props.setOrderStatus(
				getOrderStatusFromAdminStatus(currentAdminOrderStatus)
			);
			props.setOrderViewedByAdmin(
				currentAdminOrderStatus !== AdminViewOrderStatus.UNPAID
			);

			openSnackbar(
				translation("OrderStatusAndTimeUpdated"),
				Severity.SUCCESS,
				undefined,
				<GreenTickIcon />
			);
			setOpen(false);
		} catch (err: any) {
			openSnackbar(
				translation("SystemError"),
				Severity.ERROR,
				translation("SystemErrorMsg"),
				<RedErrorIcon />
			);
		} finally {
			setLoading(false);
		}
	}

	useEffect(() => {
		if (open) {
			setCurrentAdminOrderStatus(
				getAdminStatus(orderStatus, isOrderPaid) as AdminViewOrderStatus
			);
			setPayDate(paymentTime || null);
			// setAssignDate(assignTime || null);
			setExpectedCompleteTime(expectedCompleteTime || null);
			setShipDate(shipTime || null);
			setDeliverDate(deliverTime || null);
		}
	}, [open]);

	const timePickers = fieldsForDates.map((field, index) => {
		return (
			<Restricted to={field.permission} key={index}>
				<div className="modal-datepicker-row">
					<div className="typography-body-01">{field.label}</div>
					{field.dateOnly ? (
						<CustomDatePicker
							selectedDate={field.value}
							disabled={field.disabled}
							setSelectedDate={field.setValue}
							errorMsg={field.errorMsg}
						/>
					) : (
						<CustomDateTimePicker
							selectedDate={field.value}
							disabled={field.disabled}
							setSelectedDate={field.setValue}
							errorMsg={field.errorMsg}
						></CustomDateTimePicker>
					)}
				</div>
			</Restricted>
		);
	});
	const updateStatusTimeModal: React.ReactNode = (
		<ModalWithButtons
			modalWidth={500}
			headerText={translation("OrderStatusAndTime")}
			headerDescription={translation("OrderStatusAndTimeDescription")}
			open={open}
			setOpen={setOpen}
			buttons={[
				<div
					key={"update-order-status-time-button"}
					style={{
						display: "flex",
						flexDirection: "column",
						width: "100%",
						margin: "0px 20px",
					}}
				>
					<BlueButton
						id="update-order-status-time-button"
						size="flex"
						children={
							loading ? (
								<ButtonTextLoadingAnimation
									text={translation("Loading")}
								></ButtonTextLoadingAnimation>
							) : (
								translation("Update")
							)
						}
						disabled={loading}
						onClick={handleSubmit}
					></BlueButton>
				</div>,
			]}
		>
			<div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
				{statusSelectionDropdown}
				{timePickers}
			</div>
		</ModalWithButtons>
	);
	return updateStatusTimeModal;
}
